import { useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Mail, Send, X, AlertTriangle } from "lucide-react";

// HC-EPIC Monthly Grant Report parity slice — manual-recipient send modal.
// Gated STRICTLY by permission `grant_reports.email_external`. reports.view
// alone does NOT admit external delivery. The full authoritative Monthly
// Grant Report PDF (built by backend/reports/grant_report_service) is
// attached — recipient choice does NOT change report content.

const EMAIL_RE = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,63}$/;
const HEADER_INJECTION_TOKENS = ["\r", "\n", "%0a", "%0d", "%0A", "%0D",
  "bcc:", "cc:", "subject:", "from:", "to:", "content-type:"];

export function GrantReportEmailModal({ open, onClose, month, monthLabel }) {
  const { can } = useAuth();
  const authorized = can("grant_reports.email_external");

  const [recipient, setRecipient] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [sending, setSending] = useState(false);
  const [validationError, setValidationError] = useState("");

  if (!open) return null;

  // Permission-denied dialog — the modal still opens but the send action
  // is hidden and a clear explanation is shown. This keeps the UI honest
  // for a user whose role includes reports.view but NOT external delivery.
  if (!authorized) {
    return (
      <div
        data-testid="grant-report-email-modal-denied"
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      >
        <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
          <div className="flex items-start gap-3">
            <div className="rounded-xl bg-[#F59E0B]/10 p-3 text-[#F59E0B]">
              <AlertTriangle className="h-5 w-5" strokeWidth={2} />
            </div>
            <div className="flex-1">
              <h3 className="font-display text-lg font-semibold text-[#0F172A]">
                Additional permission required
              </h3>
              <p className="mt-2 text-sm text-[#334155]">
                Emailing the Monthly Grant Report to an external recipient
                requires the <code className="rounded bg-[#F9F8F6] px-1.5 py-0.5 font-mono text-xs">grant_reports.email_external</code>{" "}
                permission. Ask an administrator to grant it to your role.
              </p>
            </div>
          </div>
          <div className="mt-6 flex justify-end">
            <button
              onClick={onClose}
              data-testid="grant-report-email-modal-close-denied"
              className="rounded-full bg-[#1E3A8A] px-5 py-2 text-sm font-medium text-white hover:opacity-90"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  const validateRecipient = (raw) => {
    const v = (raw || "").trim();
    if (!v) return "Recipient is required.";
    const lowered = v.toLowerCase();
    for (const tok of HEADER_INJECTION_TOKENS) {
      if (lowered.includes(tok.toLowerCase())) return "Recipient contains invalid characters.";
    }
    if (v.length > 254) return "Recipient exceeds 254 characters.";
    if (!EMAIL_RE.test(v)) return "Enter a valid email address.";
    return "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validateRecipient(recipient);
    if (err) { setValidationError(err); return; }
    if (!confirmed) { setValidationError("Please confirm you want to send the report."); return; }
    setValidationError("");
    setSending(true);
    try {
      const { data } = await api.post("/reports/grant-report/email", {
        month,
        to: recipient.trim(),
        subject: subject.trim() || undefined,
        message: message.trim() || undefined,
        confirmed: true,
      });
      toast.success(`Grant report emailed to ${data.recipient}`);
      onClose();
      setRecipient("");
      setSubject("");
      setMessage("");
      setConfirmed(false);
    } catch (ex) {
      toast.error(formatApiError(ex));
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      data-testid="grant-report-email-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
    >
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl"
      >
        <div className="mb-5 flex items-start justify-between">
          <div className="flex items-start gap-3">
            <div className="rounded-xl bg-[#1E3A8A] p-3 text-white">
              <Mail className="h-5 w-5" strokeWidth={2} />
            </div>
            <div>
              <h3 className="font-display text-lg font-semibold text-[#0F172A]">
                Email Grant Report
              </h3>
              <p className="text-sm text-[#334155]">
                Send the <strong>{monthLabel}</strong> Monthly Grant Report PDF to a
                specific recipient.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="grant-report-email-modal-close"
            aria-label="Close"
            className="rounded-lg p-1 text-[#334155] hover:bg-[#F9F8F6]"
          >
            <X className="h-5 w-5" strokeWidth={2} />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-[#334155]">
              Recipient email <span className="text-[#F59E0B]">*</span>
            </label>
            <input
              type="email"
              value={recipient}
              onChange={(e) => setRecipient(e.target.value)}
              placeholder="grants@partner.org"
              maxLength={254}
              required
              data-testid="grant-report-email-recipient-input"
              className="w-full rounded-xl border border-[#E5E5E0] bg-white px-4 py-2.5 text-[#0F172A] focus:border-[#1E3A8A] focus:outline-none"
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-[#334155]">
              Subject <span className="text-[#7E937D]">(optional)</span>
            </label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder={`HarborWell ${monthLabel} Grant Report`}
              maxLength={200}
              data-testid="grant-report-email-subject-input"
              className="w-full rounded-xl border border-[#E5E5E0] bg-white px-4 py-2.5 text-[#0F172A] focus:border-[#1E3A8A] focus:outline-none"
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-[#334155]">
              Message to recipient <span className="text-[#7E937D]">(optional)</span>
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              maxLength={4000}
              placeholder="Thank you for supporting HarborWell — the report is attached."
              data-testid="grant-report-email-message-input"
              className="w-full resize-none rounded-xl border border-[#E5E5E0] bg-white px-4 py-2.5 text-[#0F172A] focus:border-[#1E3A8A] focus:outline-none"
            />
            <p className="mt-1 text-xs text-[#7E937D]">
              The complete authoritative PDF report is attached — this message is only a note.
            </p>
          </div>

          <label className="flex items-start gap-3 rounded-xl bg-[#F9F8F6] p-3">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              data-testid="grant-report-email-confirm-checkbox"
              className="mt-0.5 h-4 w-4"
            />
            <span className="text-sm text-[#0F172A]">
              I confirm this recipient should receive the {monthLabel} Grant Report PDF.
            </span>
          </label>

          {validationError && (
            <p
              role="alert"
              data-testid="grant-report-email-validation-error"
              className="text-sm text-[#F59E0B]"
            >
              {validationError}
            </p>
          )}
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={sending}
            data-testid="grant-report-email-modal-cancel"
            className="rounded-full border border-[#E5E5E0] px-5 py-2.5 text-sm font-medium text-[#334155] hover:bg-[#F9F8F6] disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={sending || !confirmed}
            data-testid="grant-report-email-modal-send"
            className="inline-flex items-center gap-2 rounded-full bg-[#1E3A8A] px-5 py-2.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-60"
          >
            <Send className="h-4 w-4" strokeWidth={2} />
            {sending ? "Sending…" : "Send report"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default GrantReportEmailModal;
